import {
  RecoveryCryptoError,
  validateRecoveryManifest,
  validateRecoveryPublicKey,
  validateRecoverySealedShare,
  validateRecoveryVaultEnvelope,
  type RecoveryManifest,
  type RecoverySealedShare,
  type RecoveryVaultEnvelope,
} from '../crypto/recovery.ts';
import type {
  EncryptedRecoveryManifest,
  EncryptedShareEnvelope,
  SealedShareEnvelope,
} from './types.ts';

function asError(label: string, cause: unknown): Error {
  if (cause instanceof RecoveryCryptoError) {
    return new Error(`${label}: ${cause.message}`);
  }
  return cause instanceof Error
    ? new Error(`${label}: ${cause.message}`)
    : new Error(`${label} is invalid.`);
}

export function assertEncryptedManifest(
  manifest: EncryptedRecoveryManifest
): RecoveryManifest {
  try {
    return validateRecoveryManifest(manifest);
  } catch (cause) {
    throw asError('The activation manifest', cause);
  }
}

export function assertRecoveryVaultEnvelope(
  envelope: unknown
): RecoveryVaultEnvelope {
  try {
    return validateRecoveryVaultEnvelope(envelope);
  } catch (cause) {
    throw asError('The recovery vault envelope', cause);
  }
}

export function assertEncryptedShareEnvelope(
  envelope: EncryptedShareEnvelope | SealedShareEnvelope
): RecoverySealedShare {
  try {
    return validateRecoverySealedShare(envelope);
  } catch (cause) {
    throw asError('The encrypted share envelope', cause);
  }
}

export function assertRequestBoundEnvelope(
  envelope: SealedShareEnvelope,
  requestId: string
): RecoverySealedShare {
  const sealed = assertEncryptedShareEnvelope(envelope);
  if (
    sealed.context.purpose !== 'recovery-request-share' ||
    sealed.context.requestId !== requestId
  ) {
    throw new Error(
      'The encrypted share envelope is not bound to the current recovery request.'
    );
  }
  return sealed;
}

export function assertPublicRecoveryJwk(
  jwk: JsonWebKey,
  label = 'The custody key'
): void {
  try {
    validateRecoveryPublicKey(jwk);
  } catch (cause) {
    throw asError(label, cause);
  }
}
